import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { ComplianceFiltersComponent } from '../_shared/components/compliance-filters/compliance-filters.component';
import { NonCompliantWidgetComponent } from '../_shared/widgets/non-compliant-widget/non-compliant-widget.component';
import { ExpiringSoonWidgetComponent } from '../_shared/widgets/expiring-soon-widget/expiring-soon-widget.component';
import { CompliantWidgetComponent } from '../_shared/widgets/compliant-widget/compliant-widget.component';
import { InspectionPassRateWidgetComponent } from '../_shared/widgets/inspection-pass-rate-widget/inspection-pass-rate-widget.component';
import { ComplianceOverviewDetailsComponent } from './components/compliance-overview-details/compliance-overview-details.component';
import { PeriodicComplianceTasksComponent } from './components/periodic-compliance-tasks/periodic-compliance-tasks.component';
import { RoutineComplianceTasksComponent } from './components/routine-compliance-tasks/routine-compliance-tasks.component';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { BasePageComponent } from '../../../_shared/components/page/base-page.component';
import { ComplianceStateService } from '../_shared/services/compliance-state.service';
import { ComplianceOverviewRequirementsRequest, ComplianceTab } from './models/compliance-item.model';
import { TranslocoDirective } from '@jsverse/transloco';

@Component({
  selector: 'app-compliance-overview',
  standalone: true,
  imports: [
    CommonModule,
    ComplianceFiltersComponent,
    NonCompliantWidgetComponent,
    ExpiringSoonWidgetComponent,
    CompliantWidgetComponent,
    InspectionPassRateWidgetComponent,
    ComplianceOverviewDetailsComponent,
    PeriodicComplianceTasksComponent,
    RoutineComplianceTasksComponent,
    TranslocoDirective
  ],
  templateUrl: './compliance-overview.component.html',
  styleUrl: './compliance-overview.component.scss'
})
export class ComplianceOverviewComponent extends BasePageComponent implements OnInit {
  @ViewChild(ComplianceFiltersComponent) private complianceFilters?: ComplianceFiltersComponent;

  private complianceStateService: ComplianceStateService = inject(ComplianceStateService);
  private readonly destroy$ = new Subject<void>();
  private readonly validTabs = new Set<ComplianceTab>([
    'complianceOverviewDetails',
    'periodicComplianceTasks',
    'routineComplianceTasks'
  ]);

  private readonly pageId = 'compliance-overview';
  private restoredRequest: ComplianceOverviewRequirementsRequest | null = null;
  private hasTabQueryParam: boolean = false;
  private lastPersistedFilters: string | null = null;
  private default_page_size = 10;

  activeTab: ComplianceTab = 'complianceOverviewDetails';

  override ngOnInit(): void {
    const tab = this.route.snapshot.queryParamMap.get('tab') as ComplianceTab | null;
    this.hasTabQueryParam = !!tab && this.validTabs.has(tab);
    if (this.hasTabQueryParam) {
      this.activeTab = tab!;
    }
    super.ngOnInit();

    if (this.restoredRequest) {
      this.syncFilterService();
    } else {
      this.complianceStateService.clearPageFilters({
        accountIds: true,
        assetType: this.activeTab !== 'routineComplianceTasks',
        complianceType: true,
        complianceRegion: true,
        complianceStandard: true,
        searchTerm: true
      });
      this.lastPersistedFilters = JSON.stringify(this.getFilters());
    }
    this.complianceStateService.request$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.persistFilters());
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.destroy$.next();
    this.destroy$.complete();

    if (this.activeTab === 'routineComplianceTasks') {
      this.complianceFilters?.clearAssetTypeFilter();
    }
  }

  public toggleTab(tab: ComplianceTab): void {
    if (this.activeTab === tab) {
      return;
    }

    const previousTab = this.activeTab;
    this.activeTab = tab;
    if (previousTab === 'routineComplianceTasks') {
      this.complianceFilters?.clearAssetTypeFilter();
    }

    this.persistFilters();
  }

  protected override onRefresh(): void { }

  protected override shouldRefresh(): boolean {
    return false;
  }

  protected override getFilters(): any {
    const request = this.complianceStateService.getCurrentRequest();

    return {
      pageId: this.pageId,
      activeTab: this.activeTab,
      accountIds: request?.accountIds ?? [],
      complianceId: request?.complianceId ?? null,
      complianceRegion: request?.complianceRegion ?? null,
      complianceType: request?.complianceType ?? null,
      assetTypeId: request?.assetTypeId ?? null
    };
  }

  protected override applyPageFilterState(pageFilter: any): void {
    if (pageFilter?.pageId !== this.pageId) {
      return;
    }
    const storedTab = pageFilter.activeTab as ComplianceTab | null;
    if (!this.hasTabQueryParam && storedTab && this.validTabs.has(storedTab)) {
      this.activeTab = storedTab;
    }

    const current = this.complianceStateService.getCurrentRequest();

    this.restoredRequest = {
      page: current?.page ?? 1,
      pageSize: current?.pageSize ?? this.default_page_size,
      accountIds: Array.isArray(pageFilter.accountIds) ? pageFilter.accountIds : [],
      complianceId: pageFilter.complianceId ?? undefined,
      complianceRegion: pageFilter.complianceRegion ?? undefined,
      complianceType: pageFilter.complianceType ?? undefined,
      assetTypeId: this.activeTab === 'routineComplianceTasks'
        ? pageFilter.assetTypeId ?? undefined
        : undefined
    };
  }

  private syncFilterService(): void {
    if (!this.restoredRequest) {
      return;
    }

    this.complianceStateService.updateComplianceRequest(this.restoredRequest);
    this.lastPersistedFilters = JSON.stringify(this.getFilters());
  }

  private persistFilters(): void {
    const filters = JSON.stringify(this.getFilters());
    if (filters === this.lastPersistedFilters) {
      return;
    }

    this.lastPersistedFilters = filters;
    this.updatePageFilterState();
  }
}
